import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI } from '../../services/api';
import { formatInTz } from '../../utils/timezone';

// ── Count-up hook ─────────────────────────────────────────────────────────────
function useCountUp(target, duration = 900) {
  const [value, setValue] = useState(0);
  const frame = useRef(null);
  useEffect(() => {
    if (frame.current) cancelAnimationFrame(frame.current);
    const start = performance.now();
    const from = 0;
    const animate = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(from + (target - from) * eased));
      if (progress < 1) frame.current = requestAnimationFrame(animate);
    };
    frame.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame.current);
  }, [target, duration]);
  return value;
}

const C = {
  bg:      '#F0F2F5',
  surface: '#FFFFFF',
  border:  '#DDE1E7',
  accent:  'var(--accent)',
  text:    '#1A202C',
  muted:   '#718096',
  green:   '#22c55e',
  red:     '#ef4444',
  amber:   '#f59e0b',
  blue:    '#3b82f6',
};

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

// inject keyframes once
const ANIM_STYLE = `
@keyframes fadeSlideUp { from { opacity:0; transform:translateY(18px); } to { opacity:1; transform:none; } }
@keyframes fadeIn      { from { opacity:0; } to { opacity:1; } }
@keyframes pulse       { 0%,100% { transform:scale(1); } 50% { transform:scale(1.04); } }
@keyframes shimmer     { 0% { background-position:-400px 0; } 100% { background-position:400px 0; } }
`;
if (!document.getElementById('exec-anim')) {
  const s = document.createElement('style');
  s.id = 'exec-anim';
  s.textContent = ANIM_STYLE;
  document.head.appendChild(s);
}

// ── Mini bar chart ────────────────────────────────────────────────────────────
function GroupedBar({ data }) {
  if (!data?.length) return <Empty />;
  const maxVal = Math.max(...data.flatMap(d => [d.intake, d.resolved]), 1);
  return (
    <svg viewBox={`0 0 ${data.length * 80 + 20} 160`} style={{ width: '100%', height: 160 }}>
      {[0, 0.25, 0.5, 0.75, 1].map((p, i) => (
        <g key={i}>
          <line x1={0} y1={140 - p * 120} x2={data.length * 80 + 20} y2={140 - p * 120} stroke={C.border} strokeWidth={1} />
          <text x={0} y={140 - p * 120 - 3} fill={C.muted} fontSize={9}>{Math.round(maxVal * p)}</text>
        </g>
      ))}
      {data.map((d, i) => {
        const x = 30 + i * 80;
        const intakeH  = (d.intake  / maxVal) * 120;
        const resolvedH = (d.resolved / maxVal) * 120;
        return (
          <g key={i}>
            <rect x={x}      y={140 - intakeH}   width={22} height={intakeH}   fill={C.blue}  rx={3} />
            <rect x={x + 26} y={140 - resolvedH} width={22} height={resolvedH} fill={C.green} rx={3} />
            <text x={x + 22} y={155} textAnchor="middle" fill={C.muted} fontSize={10}>{d.month}</text>
            <text x={x + 11} y={140 - intakeH   - 4} textAnchor="middle" fill={C.text}  fontSize={8}>{d.intake}</text>
            <text x={x + 37} y={140 - resolvedH - 4} textAnchor="middle" fill={C.green} fontSize={8}>{d.resolved}</text>
          </g>
        );
      })}
      <rect x={0}  y={160} width={10} height={8} fill={C.blue}  rx={2} />
      <text x={14} y={167} fill={C.muted} fontSize={9}>Intake</text>
      <rect x={56} y={160} width={10} height={8} fill={C.green} rx={2} />
      <text x={70} y={167} fill={C.muted} fontSize={9}>Resolved</text>
    </svg>
  );
}

// ── Horizontal bar chart ──────────────────────────────────────────────────────
function HBar({ data, valueKey = 'total', colorFn }) {
  if (!data?.length) return <Empty />;
  const maxVal = Math.max(...data.map(d => d[valueKey] || 0), 1);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {data.map((d, i) => {
        const pct = ((d[valueKey] || 0) / maxVal) * 100;
        const color = colorFn ? colorFn(i) : C.accent;
        return (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 110, fontSize: 11, color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flexShrink: 0 }}>{d.name || d.reason || d.dept || '—'}</div>
            <div style={{ flex: 1, background: '#DDE1E7', borderRadius: 99, overflow: 'hidden', height: 10 }}>
              <div style={{ width: `${pct}%`, background: color, height: '100%', borderRadius: 99, transition: 'width 0.6s ease' }} />
            </div>
            <div style={{ width: 36, fontSize: 11, fontWeight: 700, color: C.text, textAlign: 'right', flexShrink: 0 }}>{d[valueKey]}</div>
          </div>
        );
      })}
    </div>
  );
}

// ── Gauge chart (CSAT) ───────────────────────────────────────────────────────
function GaugeChart({ value = 0, max = 5 }) {
  const pct   = Math.min(value / max, 1);
  const angle = -210 + pct * 240;
  const r  = 70;
  const cx = 90, cy = 90;
  const toXY = (deg) => {
    const rad = ((deg - 90) * Math.PI) / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  };
  const arcPath = (startDeg, endDeg, col) => {
    const s = toXY(startDeg), e = toXY(endDeg);
    const large = Math.abs(endDeg - startDeg) > 180 ? 1 : 0;
    return <path d={`M${s.x} ${s.y} A${r} ${r} 0 ${large} 1 ${e.x} ${e.y}`} stroke={col} strokeWidth={14} fill="none" strokeLinecap="round" />;
  };
  const needle = toXY(angle);
  return (
    <svg viewBox="0 0 180 120" style={{ width: '100%', maxWidth: 220, display: 'block', margin: '0 auto' }}>
      {arcPath(-210, -30, '#DDE1E7')}
      {value > 0 && arcPath(-210, angle, pct > 0.7 ? C.green : pct > 0.4 ? C.amber : C.red)}
      <line x1={cx} y1={cy} x2={needle.x} y2={needle.y} stroke={C.text} strokeWidth={2.5} strokeLinecap="round" />
      <circle cx={cx} cy={cy} r={5} fill={C.text} />
      <text x={cx} y={cy + 22} textAnchor="middle" fill={C.text}  fontSize={22} fontWeight="bold">{value.toFixed(1)}</text>
      <text x={cx} y={cy + 36} textAnchor="middle" fill={C.muted} fontSize={9}>{`out of ${max}`}</text>
      <text x={20}  y={105} fill={C.muted} fontSize={9}>0</text>
      <text x={155} y={105} fill={C.muted} fontSize={9}>{max}</text>
    </svg>
  );
}

// ── Donut ────────────────────────────────────────────────────────────────────
function Donut({ segments }) {
  let offset = 0;
  const r = 35, circ = 2 * Math.PI * r;
  return (
    <svg viewBox="0 0 100 100" style={{ width: 120, height: 120 }}>
      {segments.map((s, i) => {
        const dash = (s.pct / 100) * circ;
        const el = (
          <circle key={i} cx={50} cy={50} r={r} fill="none" stroke={s.color} strokeWidth={18}
            strokeDasharray={`${dash} ${circ - dash}`}
            strokeDashoffset={-offset}
            transform="rotate(-90 50 50)" />
        );
        offset += dash;
        return el;
      })}
    </svg>
  );
}

function Empty() {
  return <div style={{ padding: '20px 0', textAlign: 'center', color: C.muted, fontSize: 12 }}>No data this period</div>;
}

// ── Stat card ────────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, valueColor, icon, trend }) {
  return (
    <div style={{ flex: 1, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: '14px 16px', minWidth: 140 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 800, color: valueColor || C.text, lineHeight: 1 }}>{value ?? '—'}</div>
      {sub && <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{sub}</div>}
      {trend !== undefined && (
        <div style={{ fontSize: 12, fontWeight: 600, color: trend >= 0 ? C.green : C.red, marginTop: 4 }}>
          {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}% vs last month
        </div>
      )}
    </div>
  );
}

// ── Section card ─────────────────────────────────────────────────────────────
function SectionCard({ num, title, accent, children, style: extraStyle, delay = 0 }) {
  return (
    <div style={{
      background: C.surface, border: `1.5px solid ${C.border}`, borderRadius: 14,
      padding: '16px 18px', ...extraStyle,
      animation: `fadeSlideUp 0.5s ease both`,
      animationDelay: `${delay}ms`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, borderBottom: `1px solid ${C.border}`, paddingBottom: 10 }}>
        <span style={{ width: 24, height: 24, borderRadius: 7, background: accent || C.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, color: '#fff', flexShrink: 0, boxShadow: '0 2px 8px var(--accent-bg)' }}>{num}</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: C.text, letterSpacing: '0.04em' }}>{title}</span>
      </div>
      {children}
    </div>
  );
}

// ── Animated number ───────────────────────────────────────────────────────────
function AnimNum({ value = 0, color, size = 26, suffix = '' }) {
  const display = useCountUp(value);
  return (
    <span style={{ fontSize: size, fontWeight: 800, color: color || C.text, lineHeight: 1, transition: 'color 0.3s' }}>
      {display}{suffix}
    </span>
  );
}

// ── Stars ─────────────────────────────────────────────────────────────────────
function Stars({ value = 0, max = 5 }) {
  return (
    <span>
      {Array.from({ length: max }).map((_, i) => (
        <span key={i} style={{ color: i < Math.round(value) ? C.amber : C.border, fontSize: 16 }}>★</span>
      ))}
    </span>
  );
}

const WIDGET_DEFS = [
  { id: 'kpi',          label: 'KPI Summary (Production / SLA / Productivity / Compliance)' },
  { id: 'trend',        label: 'Ticket Intake vs. Resolution Trend' },
  { id: 'slaStatus',    label: 'SLA Status & Accomplishment' },
  { id: 'agents',       label: 'Agent Performance' },
  { id: 'dept',         label: 'Tickets by Department' },
  { id: 'slaOverview',  label: 'Tickets & SLA Overview (Monthly)' },
  { id: 'topCats',      label: 'Top Reported Categories (10-month)' },
  { id: 'catTrends',    label: 'Category Trends / Patterns (Pies)' },
  { id: 'catBreakdown', label: 'Category Breakdown (Sub-categories)' },
  { id: 'agentUtil',    label: 'Agent Utilization & SLA% (Table + Bars)' },
];

function loadCeoWidgets() {
  const defaults = WIDGET_DEFS.map(w => w.id);
  try {
    const raw = localStorage.getItem('ceo_widgets');
    if (raw) {
      const saved = JSON.parse(raw);
      // Merge: keep saved order, append any new widget IDs introduced later
      const merged = [...saved];
      for (const id of defaults) if (!merged.includes(id)) merged.push(id);
      return merged;
    }
  } catch {}
  return defaults;
}

// ─── StatLink helper – a stat row that navigates on click ──────────────────
function StatLink({ label, value, onClick }) {
  return (
    <div
      onClick={onClick}
      title="Click to view in Tickets"
      style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        cursor: onClick ? 'pointer' : 'default',
        borderRadius: 6, padding: '2px 4px', margin: '-2px -4px',
        transition: 'background 0.15s',
      }}
      onMouseEnter={e => { if (onClick) e.currentTarget.style.background = '#F0F4FF'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
    >
      <span style={{ fontSize: 12, color: C.muted }}>{label}</span>
      {value}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
export default function ExecutiveDashboard({ currentUser }) {
  const [selectedMonth, setSelectedMonth] = useState('all');  // 'all' or 1-12
  const [selectedDept, setSelectedDept] = useState('All');
  const [data, setData] = useState(null);
  const [overview, setOverview] = useState(null);
  const [focusCategory, setFocusCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const lastRefresh = useRef(new Date());
  const navigate = useNavigate();
  const [showCustomize, setShowCustomize] = useState(false);
  const [visibleWidgets, setVisibleWidgets] = useState(loadCeoWidgets);

  const toggleWidget = (id) => {
    setVisibleWidgets(prev => {
      const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
      try { localStorage.setItem('ceo_widgets', JSON.stringify(next)); } catch {}
      return next;
    });
  };
  const wv = (id) => visibleWidgets.includes(id);

  const load = () => {
    setLoading(true);
    const now = new Date();
    const focusYear  = now.getFullYear();
    const focusMonth = selectedMonth === 'all' ? (now.getMonth() + 1) : selectedMonth;
    Promise.all([
      dashboardAPI.executive({ month: selectedMonth === 'all' ? 'all' : selectedMonth, dept: selectedDept !== 'All' ? selectedDept : undefined }),
      dashboardAPI.execOverview({ year: focusYear, month: focusMonth, months: 4, top_months: 10, trend_months: 4, category: focusCategory || undefined }),
    ])
      .then(([d, o]) => { setData(d); setOverview(o); lastRefresh.current = new Date(); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [selectedMonth, selectedDept, focusCategory]);

  // Auto-refresh every 5 min
  useEffect(() => {
    const t = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [selectedMonth, selectedDept]);

  const prod     = data?.production  || {};
  const sla      = data?.sla         || {};
  const agents   = data?.agents      || [];
  const trend    = data?.monthlyTrend || [];
  const breachR  = data?.slaBreachReasons || [];
  const total    = prod.total || 0;
  const resolved = prod.resolved || 0;
  const slaBreached = sla.breached || 0;
  const slaMetRate  = sla.metRate  || 0;
  const activeAgents = agents.filter(a => a.total > 0);
  const avgUtil  = activeAgents.length ? Math.round(activeAgents.reduce((s, a) => s + a.utilizationPct, 0) / activeAgents.length) : 0;
  const csatData = data?.csat || {};
  const avgCsat  = csatData.avg != null ? csatData.avg : null;
  const csatHighlySat   = csatData.highlySatisfied    || 0;
  const csatSatisfied   = csatData.satisfied          || 0;
  const csatNeutral     = csatData.neutral            || 0;
  const csatDissat      = csatData.dissatisfied       || 0;
  const csatHighlyDis   = csatData.highlyDissatisfied || 0;
  const csatRated       = csatData.ratedCount         || 0;
  const agentColors = [C.accent, C.blue, C.green, C.amber, '#a855f7', '#ec4899', '#14b8a6', '#f97316', '#84cc16', '#3B5BDB'];

  return (
    <div style={{ height: '100%', overflowY: 'auto', background: C.bg, padding: 0 }}>
      {/* ── Header ── */}
      <div style={{ background: '#FFFFFF', borderBottom: `1px solid ${C.border}`, padding: '0 24px', height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: 8, background: C.accent, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontSize: 16 }}>📊</span>
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, color: C.text, letterSpacing: '-0.01em' }}>Executive Overview</div>
            <div style={{ fontSize: 11, color: C.muted }}>
              Last refresh: {formatInTz(lastRefresh.current, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <select value={selectedMonth} onChange={e => setSelectedMonth(e.target.value === 'all' ? 'all' : Number(e.target.value))}
            style={{ background: '#F8FAFC', border: `1.5px solid ${C.border}`, borderRadius: 8, padding: '6px 10px', color: C.text, fontSize: 12, outline: 'none', cursor: 'pointer', fontWeight: 600 }}>
            <option value="all">All Time</option>
            {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
          <select value={selectedDept} onChange={e => setSelectedDept(e.target.value)}
            style={{ background: '#F8FAFC', border: `1.5px solid ${C.border}`, borderRadius: 8, padding: '6px 10px', color: C.text, fontSize: 12, outline: 'none', cursor: 'pointer', fontWeight: 600 }}>
            {['All','IT','HR','Finance','Engineering','Marketing','Sales','Operations','Legal'].map(d => <option key={d}>{d}</option>)}
          </select>
          <button onClick={() => setShowCustomize(p => !p)} style={{ background: showCustomize ? 'var(--accent-bg)' : '#F8FAFC', border: `1.5px solid ${showCustomize ? 'var(--accent)' : C.border}`, borderRadius: 8, padding: '7px 14px', color: showCustomize ? 'var(--accent)' : C.muted, fontWeight: 700, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
            ⚙ Customize
          </button>
          <button onClick={load} style={{ background: 'var(--accent)', border: 'none', borderRadius: 8, padding: '7px 16px', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
            ↻ Refresh
          </button>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', background: 'var(--accent-bg)', padding: '6px 12px', borderRadius: 8, border: `1px solid var(--border)` }}>
            👤 {currentUser?.name || 'CEO Executive'}
          </div>
        </div>
      </div>

      {loading && (
        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14 }}>
            {[0,1,2,3].map(i => (
              <div key={i} style={{ borderRadius: 14, height: 160, background: 'linear-gradient(90deg,#f0f0f0 25%,#e8e8e8 50%,#f0f0f0 75%)', backgroundSize: '800px 100%', animation: 'shimmer 1.4s infinite linear', animationDelay: `${i * 0.1}s` }} />
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 14 }}>
            <div style={{ borderRadius: 14, height: 220, background: 'linear-gradient(90deg,#f0f0f0 25%,#e8e8e8 50%,#f0f0f0 75%)', backgroundSize: '800px 100%', animation: 'shimmer 1.4s infinite linear' }} />
            <div style={{ borderRadius: 14, height: 220, background: 'linear-gradient(90deg,#f0f0f0 25%,#e8e8e8 50%,#f0f0f0 75%)', backgroundSize: '800px 100%', animation: 'shimmer 1.4s 0.1s infinite linear' }} />
          </div>
        </div>
      )}

      {/* ── Customize panel ── */}
      {showCustomize && (
        <div style={{ background: '#F8FAFC', borderBottom: `1px solid ${C.border}`, padding: '14px 24px', display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, fontWeight: 800, color: C.text, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Show / Hide Widgets</span>
          {WIDGET_DEFS.map(w => (
            <label key={w.id} style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: visibleWidgets.includes(w.id) ? C.accent : C.muted }}>
              <div onClick={() => toggleWidget(w.id)} style={{ width: 16, height: 16, borderRadius: 4, border: `2px solid ${visibleWidgets.includes(w.id) ? C.accent : C.border}`, background: visibleWidgets.includes(w.id) ? C.accent : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.15s' }}>
                {visibleWidgets.includes(w.id) && <svg width="9" height="9" viewBox="0 0 12 12" fill="none"><polyline points="2,6 5,9 10,3" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"/></svg>}
              </div>
              {w.label}
            </label>
          ))}
          <button onClick={() => { setVisibleWidgets(WIDGET_DEFS.map(w => w.id)); localStorage.setItem('ceo_widgets', JSON.stringify(WIDGET_DEFS.map(w => w.id))); }} style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, color: C.muted, border: `1px solid ${C.border}`, background: '#fff', borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}>Reset All</button>
        </div>
      )}

      {!loading && (
        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* ── Row 1: KPI sections ── */}
          {wv('kpi') && <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
            {/* 1 Production */}
            <SectionCard num="1" title="PRODUCTION" accent={C.blue} delay={0}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <StatLink label="Total Tickets" value={<AnimNum value={total} />} onClick={() => navigate('/tickets')} />
                <StatLink label="Open" value={<AnimNum value={prod.open ?? 0} color={C.amber} size={20} />} onClick={() => navigate('/tickets?status=Open')} />
                <StatLink label="Resolved" value={<AnimNum value={resolved} color={C.green} />} onClick={() => navigate('/tickets?status=Completed')} />
                <div style={{ background: '#EBF0FF', borderRadius: 8, padding: '6px 10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 11, color: C.muted }}>Resolution Rate</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: total > 0 ? C.green : C.muted }}>
                      {total > 0 ? ((resolved / total) * 100).toFixed(1) : 0}%
                    </span>
                  </div>
                </div>
              </div>
            </SectionCard>

            {/* 2 SLA */}
            <SectionCard num="2" title="SLA" accent={C.amber} delay={80}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span style={{ fontSize: 12, color: C.muted }}>SLA Met Rate</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <AnimNum value={slaMetRate} color={slaMetRate >= 95 ? C.green : slaMetRate >= 80 ? C.amber : C.red} suffix="%" />
                    <span style={{ fontSize: 16 }}>{slaMetRate >= 95 ? '↗' : '↘'}</span>
                  </div>
                </div>
                <div style={{ background: '#DDE1E7', height: 8, borderRadius: 99, overflow: 'hidden' }}>
                  <div style={{ width: `${slaMetRate}%`, background: slaMetRate >= 95 ? C.green : slaMetRate >= 80 ? C.amber : C.red, height: '100%', borderRadius: 99, transition: 'width 1.2s cubic-bezier(.4,0,.2,1)' }} />
                </div>
                <StatLink
                  label="Breached"
                  value={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <AnimNum value={slaBreached} color={slaBreached > 0 ? C.red : C.green} />
                      {slaBreached > 0 && <span style={{ fontSize: 18, animation: 'pulse 1.5s infinite' }}>⚠</span>}
                    </div>
                  }
                  onClick={() => navigate('/tickets?overdue=1')}
                />
                <div style={{ background: '#EBF0FF', borderRadius: 8, padding: '6px 10px', fontSize: 11, color: C.muted, cursor: 'pointer' }}
                  onClick={() => navigate('/tickets')}
                  title="Click to view all tickets">
                  {sla.met ?? 0} met SLA of {sla.total ?? 0} total
                </div>
              </div>
            </SectionCard>

            {/* 3 Productivity */}
            <SectionCard num="3" title="PRODUCTIVITY" accent={C.accent} delay={160}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                    <span style={{ fontSize: 12, color: C.muted }}>Avg Utilization</span>
                    <AnimNum value={avgUtil} color={avgUtil >= 75 ? C.green : avgUtil >= 50 ? C.amber : C.red} suffix="%" />
                  </div>
                  <div style={{ fontSize: 10, color: C.muted, marginBottom: 4 }}>Target: 75%</div>
                  <div style={{ background: '#DDE1E7', height: 8, borderRadius: 99, overflow: 'hidden' }}>
                    <div style={{ width: `${Math.min(avgUtil, 100)}%`, background: avgUtil >= 75 ? C.green : C.amber, height: '100%', borderRadius: 99, transition: 'width 1.2s cubic-bezier(.4,0,.2,1)' }} />
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span style={{ fontSize: 12, color: C.muted }}>Active Agents</span>
                  <AnimNum value={agents.filter(a => a.total > 0).length} color={C.accent} size={22} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span style={{ fontSize: 12, color: C.muted }}>Avg Tickets/Agent</span>
                  <span style={{ fontSize: 18, fontWeight: 700, color: C.text }}>
                    {agents.length > 0 ? (total / agents.length).toFixed(1) : '—'}
                  </span>
                </div>
              </div>
            </SectionCard>

            {/* 4 Compliance */}
            <SectionCard num="4" title="COMPLIANCE" accent={C.green} delay={240}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span style={{ fontSize: 12, color: C.muted }}>Avg CSAT</span>
                  <span style={{ fontSize: 26, fontWeight: 800, color: C.amber }}>{avgCsat != null ? `${avgCsat} / 5.0` : '— / 5.0'}</span>
                </div>
                <Stars value={avgCsat || 0} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span style={{ fontSize: 12, color: C.muted }}>Remark Completeness</span>
                  <span style={{ fontSize: 18, fontWeight: 700, color: C.green }}>100%</span>
                </div>
                <div style={{ background: '#EBF0FF', borderRadius: 8, padding: '6px 10px', fontSize: 11, color: C.muted }}>
                  Based on {resolved} resolved tickets this period
                </div>
              </div>
            </SectionCard>
          </div>}

          {/* ── Row 2: Charts ── */}
          {(wv('trend') || wv('slaStatus')) && <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 14, animation: 'fadeSlideUp 0.55s ease 0.25s both' }}>
            {/* Trend chart */}
            {wv('trend') && <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: '16px 18px' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 12 }}>
                Total Ticket Intake vs. Resolution Trend
              </div>
              <GroupedBar data={trend} />
            </div>}

            {/* SLA Status */}
            {wv('slaStatus') && <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: '16px 18px', display: 'flex', flexDirection: 'column' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 12 }}>
                SLA Status & Accomplishment
              </div>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {/* Stacked visual */}
                <div style={{ background: '#DDE1E7', borderRadius: 10, overflow: 'hidden', height: 28 }}>
                  <div style={{ display: 'flex', height: '100%' }}>
                    <div style={{ flex: sla.met || 1, background: C.green, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#F0F2F5' }}>
                      {sla.met ?? 0}
                    </div>
                    {slaBreached > 0 && (
                      <div style={{ flex: slaBreached, background: C.red, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#fff', minWidth: 30 }}>
                        {slaBreached}
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 16, fontSize: 11 }}>
                  <span style={{ color: C.green }}>● Met: {sla.met ?? 0} ({slaMetRate}%)</span>
                  <span style={{ color: C.red }}>● Breached: {slaBreached} ({sla.total ? ((slaBreached / sla.total) * 100).toFixed(1) : 0}%)</span>
                </div>
                {/* SLA Breaches by Reason */}
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    SLA Breaches by Reason (Top {breachR.length || 3})
                  </div>
                  {breachR.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                      {breachR.map((r, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                          <span style={{ color: C.muted }}>{r.reason}</span>
                          <span style={{ fontWeight: 700, color: C.red }}>{r.count}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: C.green }}>✓ No SLA breaches this period</div>
                  )}
                </div>
              </div>
            </div>}
          </div>}

          {/* ── Row 3: Agent + CSAT ── */}
          {wv('agents') && <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 14, animation: 'fadeSlideUp 0.55s ease 0.35s both' }}>
            {/* Agent Volume */}
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: '16px 18px' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 4 }}>
                Agent Volume & Avg Utilization (Top {Math.min(agents.length, 10)})
              </div>
              <div style={{ display: 'flex', gap: 16, fontSize: 10, color: C.muted, marginBottom: 12 }}>
                <span>● Total Volume</span><span style={{ color: C.green }}>● Utilization %</span>
              </div>
              {agents.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {agents.map((a, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 120, fontSize: 11, color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flexShrink: 0 }}>{a.name}</div>
                      <div style={{ flex: 1, display: 'flex', gap: 4 }}>
                        {/* Volume bar */}
                        <div style={{ flex: 1, background: '#DDE1E7', borderRadius: 99, overflow: 'hidden', height: 8 }}>
                          <div style={{ width: `${(a.total / (agents[0]?.total || 1)) * 100}%`, background: agentColors[i % agentColors.length], height: '100%', borderRadius: 99 }} />
                        </div>
                        {/* Utilization bar */}
                        <div style={{ flex: 1, background: '#DDE1E7', borderRadius: 99, overflow: 'hidden', height: 8 }}>
                          <div style={{ width: `${a.utilizationPct}%`, background: a.utilizationPct >= 75 ? C.green : C.amber, height: '100%', borderRadius: 99 }} />
                        </div>
                      </div>
                      <div style={{ width: 30, fontSize: 11, fontWeight: 700, color: C.text, textAlign: 'right', flexShrink: 0 }}>{a.total}</div>
                      <div style={{ width: 38, fontSize: 11, fontWeight: 700, color: a.utilizationPct >= 75 ? C.green : C.amber, textAlign: 'right', flexShrink: 0 }}>{a.utilizationPct}%</div>
                    </div>
                  ))}
                </div>
              ) : <Empty />}
            </div>

            {/* CSAT */}
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: '16px 18px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 8, alignSelf: 'flex-start' }}>
                Customer Satisfaction (CSAT) Score
              </div>
              {csatRated === 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '40px 0', color: C.muted, fontSize: 13 }}>
                  <div style={{ fontSize: 32, fontWeight: 800, color: C.muted }}>—</div>
                  <div>No CSAT ratings received yet for this period.</div>
                </div>
              ) : (<>
                <GaugeChart value={avgCsat || 0} max={5} />
                <div style={{ display: 'flex', gap: 14, marginTop: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
                  <div style={{ fontSize: 11, textAlign: 'center' }}>
                    <div style={{ color: C.green, fontWeight: 700 }}>{csatHighlySat}%</div>
                    <div style={{ color: C.muted }}>Highly Satisfied</div>
                  </div>
                  <div style={{ fontSize: 11, textAlign: 'center' }}>
                    <div style={{ color: C.accent, fontWeight: 700 }}>{csatSatisfied}%</div>
                    <div style={{ color: C.muted }}>Satisfied</div>
                  </div>
                  <div style={{ fontSize: 11, textAlign: 'center' }}>
                    <div style={{ color: C.amber, fontWeight: 700 }}>{csatNeutral}%</div>
                    <div style={{ color: C.muted }}>Neutral</div>
                  </div>
                  {csatDissat > 0 && (
                    <div style={{ fontSize: 11, textAlign: 'center' }}>
                      <div style={{ color: '#F59E0B', fontWeight: 700 }}>{csatDissat}%</div>
                      <div style={{ color: C.muted }}>Dissatisfied</div>
                    </div>
                  )}
                  {csatHighlyDis > 0 && (
                    <div style={{ fontSize: 11, textAlign: 'center' }}>
                      <div style={{ color: '#EF4444', fontWeight: 700 }}>{csatHighlyDis}%</div>
                      <div style={{ color: C.muted }}>Very Dissatisfied</div>
                    </div>
                  )}
                </div>
                {/* Feedback donut */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
                  <Donut segments={[
                    { pct: csatHighlySat, color: C.green },
                    { pct: csatSatisfied, color: C.accent },
                    { pct: csatNeutral,   color: C.amber },
                    { pct: csatDissat,    color: '#F59E0B' },
                    { pct: csatHighlyDis, color: '#EF4444' },
                  ].filter(s => s.pct > 0)} />
                  <div style={{ fontSize: 10, color: C.muted, display: 'flex', flexDirection: 'column', gap: 5 }}>
                    {csatHighlySat > 0 && <span><span style={{ color: C.green }}>●</span> Highly Satisfied: {csatHighlySat}%</span>}
                    {csatSatisfied > 0 && <span><span style={{ color: C.accent }}>●</span> Satisfied: {csatSatisfied}%</span>}
                    {csatNeutral   > 0 && <span><span style={{ color: C.amber }}>●</span> Neutral: {csatNeutral}%</span>}
                    {csatDissat    > 0 && <span><span style={{ color: '#F59E0B' }}>●</span> Dissatisfied: {csatDissat}%</span>}
                    {csatHighlyDis > 0 && <span><span style={{ color: '#EF4444' }}>●</span> Very Dissatisfied: {csatHighlyDis}%</span>}
                  </div>
                </div>
                <div style={{ fontSize: 10, color: C.muted, marginTop: 8 }}>Based on {csatRated} rated ticket{csatRated === 1 ? '' : 's'}</div>
              </>)}
            </div>
          </div>}

          {/* ── Row 4: By Department ── */}
          {wv('dept') && data?.byDept?.length > 0 && (
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: '16px 18px', animation: 'fadeSlideUp 0.55s ease 0.45s both' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 12 }}>Ticket Volume by Department</div>
              <HBar data={data.byDept.map(d => ({ name: d.dept, total: parseInt(d.total, 10) }))} colorFn={i => agentColors[i % agentColors.length]} />
            </div>
          )}

          {/* ── Row 5: Tickets & SLA Overview (4-month) ── */}
          {wv('slaOverview') && overview?.monthlyOverview?.length > 0 && (
            <SlaOverviewSection rows={overview.monthlyOverview} navigate={navigate} />
          )}

          {/* ── Row 6: Top Categories (10-month pivot) ── */}
          {wv('topCats') && overview?.topCategories?.length > 0 && (
            <TopCategoriesSection
              rows={overview.topCategories}
              activeCategory={focusCategory}
              onSelect={(c) => setFocusCategory(focusCategory === c ? null : c)}
              navigate={navigate}
            />
          )}

          {/* ── Row 7: Category Trends (per-month pies) ── */}
          {wv('catTrends') && overview?.categoryTrends?.length > 0 && (
            <CategoryTrendsSection months={overview.categoryTrends} />
          )}

          {/* ── Row 8: Category sub-breakdown for focused category ── */}
          {wv('catBreakdown') && overview?.categoryBreakdown && (
            <CategoryBreakdownSection data={overview.categoryBreakdown} focusMonth={overview.focus} onClear={() => setFocusCategory(null)} />
          )}

          {/* ── Row 9: Agent Utilization + SLA% (mirrors Apr-2026 reference) ── */}
          {wv('agentUtil') && overview?.agents?.length > 0 && (
            <AgentUtilizationSection agents={overview.agents} focus={overview.focus} colors={agentColors} navigate={navigate} />
          )}

        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// New sections backed by /api/dashboard/exec-overview
// ─────────────────────────────────────────────────────────────────────────────

function SlaOverviewSection({ rows, navigate }) {
  const max = Math.max(1, ...rows.flatMap(r => [r.total, r.resolved, r.pendingApproval, r.pendingAction, r.breached]));
  const series = [
    { key: 'total',           label: 'Total Tickets Analyzed', color: C.blue },
    { key: 'resolved',        label: 'No. Resolved/Closed',    color: '#86c34a' },
    { key: 'pendingApproval', label: 'Pending Approval',       color: '#6f42c1' },
    { key: 'pendingAction',   label: 'No. Pending Action',     color: '#f0a04b' },
    { key: 'breached',        label: 'No. Breached SLA',       color: '#0ea5e9' },
  ];

  // Map metric key → URL params for ticket listing navigation
  const metricNav = {
    total:           { status: null,        slaBreached: false },
    resolved:        { status: 'Completed,Pending User Acceptance', slaBreached: false },
    pendingApproval: { status: 'Pending Approval', slaBreached: false },
    pendingAction:   { status: 'Open,Pending Action',   slaBreached: false },
    breached:        { status: null,        slaBreached: true  },
  };

  const goToTickets = (row, metricKey) => {
    if (!navigate) return;
    const from = `${row.year}-${String(row.month).padStart(2, '0')}-01`;
    const lastDay = new Date(row.year, row.month, 0).getDate();
    const to   = `${row.year}-${String(row.month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    const p = new URLSearchParams({ from, to });
    const nav = metricNav[metricKey] || {};
    if (nav.status) p.set('status', nav.status);
    if (nav.slaBreached) p.set('sla_status', 'breached');
    navigate(`/tickets?${p.toString()}`);
  };

  const clickableNum = (row, metricKey, value) => (
    value > 0
      ? <span
          onClick={() => goToTickets(row, metricKey)}
          style={{ cursor: 'pointer', color: '#1e3a8a', fontWeight: 700, textDecoration: 'underline', textUnderlineOffset: 2 }}
          title="Click to view tickets"
        >{value}</span>
      : <span style={{ fontWeight: 700 }}>{value}</span>
  );
  const W = Math.max(640, rows.length * 200);
  const groupW = (W - 80) / rows.length;
  const barW = Math.min(22, (groupW - 30) / series.length);
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: '16px 18px', animation: 'fadeSlideUp 0.55s ease both' }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: '#1e3a8a', textAlign: 'center', marginBottom: 12 }}>Tickets and SLA Overview</div>
      <div style={{ overflowX: 'auto' }}>
        <svg viewBox={`0 0 ${W} 230`} style={{ width: '100%', minWidth: 640, height: 230 }}>
          {[0, 0.25, 0.5, 0.75, 1].map((p, i) => (
            <g key={i}>
              <line x1={50} y1={180 - p * 160} x2={W} y2={180 - p * 160} stroke={C.border} strokeWidth={0.5} />
              <text x={44} y={180 - p * 160 + 3} textAnchor="end" fontSize={10} fill={C.muted}>{Math.round(max * p)}</text>
            </g>
          ))}
          {rows.map((row, ri) => {
            const baseX = 60 + ri * groupW;
            return (
              <g key={ri}>
                {series.map((s, si) => {
                  const v = row[s.key] || 0;
                  const h = (v / max) * 160;
                  const x = baseX + si * (barW + 4);
                  return (
                    <g key={si}>
                      <rect x={x} y={180 - h} width={barW} height={h} fill={s.color} rx={2}>
                        <title>{`${s.label}: ${v}`}</title>
                      </rect>
                      <text x={x + barW / 2} y={180 - h - 4} textAnchor="middle" fontSize={9} fill={C.text}>{v}</text>
                    </g>
                  );
                })}
                <text x={baseX + (groupW - 30) / 2} y={200} textAnchor="middle" fontSize={11} fontWeight={700} fill={C.text}>{row.label}</text>
              </g>
            );
          })}
        </svg>
      </div>
      {/* Legend */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, justifyContent: 'center', fontSize: 11, color: C.muted, marginBottom: 10 }}>
        {series.map(s => (
          <span key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 12, height: 12, background: s.color, borderRadius: 2 }} /> {s.label}
          </span>
        ))}
      </div>
      {/* Detailed table */}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: '#1e3a8a', color: '#fff' }}>
            <th style={thS}>Month</th>
            {rows.map(r => <th key={r.label} style={thS}>{r.label}</th>)}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={{ ...tdS, fontWeight: 700 }}>Total Tickets Analyzed</td>
            {rows.map(r => <td key={r.label} style={{ ...tdS, textAlign: 'center', fontWeight: 700 }}>{clickableNum(r, 'total', r.total)}</td>)}
          </tr>
          {[
            { label: 'No. Resolved/Closed', count: 'resolved', pct: 'resolvedPct', key: 'resolved' },
            { label: 'No. Pending Approval', count: 'pendingApproval', pct: 'pendingApprovalPct', key: 'pendingApproval' },
            { label: 'No. Pending Action',  count: 'pendingAction',   pct: 'pendingActionPct',   key: 'pendingAction' },
            { label: 'No. Breached SLA',    count: 'breached',        pct: 'breachedPct',        key: 'breached', highlight: true },
          ].map(metric => (
            <tr key={metric.label}>
              <td style={{ ...tdS, background: metric.highlight ? '#dbeafe' : '#f8fafc' }}>{metric.label}</td>
              {rows.map(r => (
                <td key={r.label} style={{ ...tdS, background: metric.highlight ? '#dbeafe' : undefined }}>
                  <div style={{ display: 'flex', justifyContent: 'space-around' }}>
                    {clickableNum(r, metric.key, r[metric.count])}
                    <span style={{ color: C.muted }}>{r[metric.pct]}%</span>
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const thS = { padding: '8px 10px', textAlign: 'center', fontSize: 11, fontWeight: 700, border: '1px solid #e2e8f0' };
const tdS = { padding: '6px 10px', border: '1px solid #e2e8f0', fontSize: 12 };

function TopCategoriesSection({ rows, activeCategory, onSelect, navigate }) {
  if (!rows.length) return null;
  const months = rows[0].monthly;

  const goCategory = (category, monthKey) => {
    if (!navigate) return;
    const p = new URLSearchParams({ category });
    if (monthKey) {
      const [y, mo] = monthKey.split('-').map(Number);
      const from = `${y}-${String(mo).padStart(2, '0')}-01`;
      const lastDay = new Date(y, mo, 0).getDate();
      const to = `${y}-${String(mo).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      p.set('from', from);
      p.set('to', to);
    }
    navigate(`/tickets?${p.toString()}`);
  };

  const clickCell = (value, category, monthKey) => {
    if (!value) return <span style={{ color: '#94a3b8' }}>-</span>;
    return (
      <span
        onClick={e => { e.stopPropagation(); goCategory(category, monthKey); }}
        style={{ cursor: 'pointer', color: '#1e3a8a', fontWeight: 700, textDecoration: 'underline', textUnderlineOffset: 2 }}
        title="Click to view tickets"
      >{value}</span>
    );
  };
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: '16px 18px', animation: 'fadeSlideUp 0.55s ease both' }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: '#1e3a8a', textAlign: 'center', marginBottom: 4 }}>Tickets Reported Categories &amp; Trends</div>
      <div style={{ fontSize: 12, color: C.muted, textAlign: 'center', marginBottom: 12 }}>Top Categories — last {months.length} months (click a row to drill down)</div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 720 }}>
          <thead>
            <tr style={{ background: '#bfdbfe', color: '#1e3a8a' }}>
              <th style={{ ...thS, textAlign: 'left' }}>Category</th>
              {months.slice().reverse().map(m => <th key={m.key} style={thS}>{m.label}</th>)}
              <th style={thS}>Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const isActive = activeCategory && r.category.toLowerCase() === activeCategory.toLowerCase();
              return (
                <tr key={r.category}
                    onClick={() => onSelect?.(r.category)}
                    style={{ cursor: 'pointer', background: isActive ? '#fef3c7' : undefined }}>
                  <td style={{ ...tdS, fontWeight: 700, color: C.text }}>{r.category}</td>
                  {r.monthly.slice().reverse().map(m => (
                    <td key={m.key} style={{ ...tdS, textAlign: 'center' }}>{clickCell(m.count, r.category, m.key)}</td>
                  ))}
                  <td style={{ ...tdS, textAlign: 'center', fontWeight: 800, color: '#1e3a8a' }}>{clickCell(r.total, r.category, null)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CategoryTrendsSection({ months }) {
  const palette = ['#3b82f6', '#dc2626', '#86c34a', '#6f42c1', '#22d3ee', '#f97316'];
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: '16px 18px', animation: 'fadeSlideUp 0.55s ease both' }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: '#1e3a8a', textAlign: 'center', marginBottom: 4 }}>Trends / Patterns</div>
      <div style={{ fontSize: 12, color: C.muted, textAlign: 'center', marginBottom: 14 }}>Repeated issues / recurring categories</div>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${months.length}, 1fr)`, gap: 14 }}>
        {months.map((m) => (
          <div key={m.key} style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 700, color: C.text, marginBottom: 6 }}>{m.label}</div>
            <PieWithLabels slices={m.slices.map((s, i) => ({ ...s, color: palette[i % palette.length] }))} />
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 3, marginTop: 8, fontSize: 10, color: C.muted }}>
              {m.slices.map((s, i) => (
                <span key={s.name} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ width: 10, height: 10, background: palette[i % palette.length], borderRadius: 2 }} /> {s.name}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PieWithLabels({ slices }) {
  const total = slices.reduce((s, x) => s + x.count, 0);
  if (total === 0) return <div style={{ height: 130, color: C.muted, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>No data</div>;
  const r = 50, cx = 70, cy = 70;
  let start = 0;
  const arc = (pct) => {
    const endAngle = start + pct * 2 * Math.PI;
    const x1 = cx + r * Math.cos(start), y1 = cy + r * Math.sin(start);
    const x2 = cx + r * Math.cos(endAngle), y2 = cy + r * Math.sin(endAngle);
    const large = pct > 0.5 ? 1 : 0;
    const midAngle = start + pct * Math.PI;
    const labelX = cx + (r * 0.6) * Math.cos(midAngle);
    const labelY = cy + (r * 0.6) * Math.sin(midAngle);
    start = endAngle;
    return { d: `M${cx},${cy} L${x1},${y1} A${r},${r} 0 ${large} 1 ${x2},${y2} Z`, labelX, labelY };
  };
  return (
    <svg viewBox="0 0 140 140" style={{ width: '100%', maxWidth: 160 }}>
      {slices.map((s) => {
        const pct = s.count / total;
        const a = arc(pct);
        return (
          <g key={s.name}>
            <path d={a.d} fill={s.color} stroke="#fff" strokeWidth={1} />
            {pct >= 0.05 && (
              <text x={a.labelX} y={a.labelY} fontSize={9} fontWeight={700} fill="#fff" textAnchor="middle">{s.pct}%</text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

function CategoryBreakdownSection({ data, focusMonth, onClear }) {
  const monthLabel = focusMonth ? new Date(focusMonth.year, focusMonth.month - 1).toLocaleString('en-US', { month: 'short', year: 'numeric' }) : '';
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: '16px 18px', animation: 'fadeSlideUp 0.55s ease both' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 800, color: '#1e3a8a' }}>Category Breakdown — {data.category}</div>
          <div style={{ fontSize: 11, color: C.muted }}>{monthLabel} • {data.total} total tickets</div>
        </div>
        <button onClick={onClear} style={{ background: '#f1f5f9', border: `1px solid ${C.border}`, borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 11, color: C.muted }}>Clear</button>
      </div>
      {data.items.length === 0 ? (
        <div style={{ padding: 20, textAlign: 'center', color: C.muted, fontSize: 12 }}>No tickets for this category in selected month.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#bfdbfe', color: '#1e3a8a' }}>
              <th style={{ ...thS, textAlign: 'left' }}>Sub-category</th>
              <th style={thS}>Count</th>
              <th style={thS}>%</th>
              <th style={{ ...thS, textAlign: 'left' }}>Distribution</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map(it => (
              <tr key={it.subcategory}>
                <td style={tdS}>{it.subcategory}</td>
                <td style={{ ...tdS, textAlign: 'center', fontWeight: 700 }}>{it.count}</td>
                <td style={{ ...tdS, textAlign: 'center', color: C.muted }}>{it.pct}%</td>
                <td style={tdS}>
                  <div style={{ background: '#e2e8f0', height: 8, borderRadius: 99, overflow: 'hidden' }}>
                    <div style={{ width: `${it.pct}%`, height: '100%', background: C.accent }} />
                  </div>
                </td>
              </tr>
            ))}
            <tr style={{ background: '#dbeafe', fontWeight: 700 }}>
              <td style={tdS}>Grand Total</td>
              <td style={{ ...tdS, textAlign: 'center' }}>{data.total}</td>
              <td style={{ ...tdS, textAlign: 'center' }}>100%</td>
              <td style={tdS} />
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
}

function AgentUtilizationSection({ agents, focus, colors, navigate }) {
  const monthLabel = focus ? new Date(focus.year, focus.month - 1).toLocaleString('en-US', { month: 'short', year: 'numeric' }) : '';

  const goAgent = (agentName, pendingOnly) => {
    if (!navigate || !focus) return;
    const from = `${focus.year}-${String(focus.month).padStart(2, '0')}-01`;
    const lastDay = new Date(focus.year, focus.month, 0).getDate();
    const to = `${focus.year}-${String(focus.month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    const p = new URLSearchParams({ from, to, assignee: agentName });
    if (pendingOnly) p.set('status', 'Open,Pending Action');
    navigate(`/tickets?${p.toString()}`);
  };

  const clickNum = (value, agentName, pendingOnly) => (
    value > 0
      ? <span
          onClick={() => goAgent(agentName, pendingOnly)}
          style={{ cursor: 'pointer', color: '#1e3a8a', fontWeight: 700, textDecoration: 'underline', textUnderlineOffset: 2 }}
          title="Click to view tickets"
        >{value}</span>
      : value
  );
  const maxHandled = Math.max(1, ...agents.map(a => a.total));
  const maxUtil = Math.max(100, ...agents.map(a => a.utilizationPct));
  // Aggregates
  const totals = agents.reduce((t, a) => {
    t.scheduled  += a.scheduledHours;
    t.srHours    += a.srHours;
    t.nonSrHours += a.nonSrHours;
    t.totalHours += a.totalHours;
    t.srCount    += a.srCount;
    t.nonSrCount += a.nonSrCount;
    t.total      += a.total;
    t.pending    += a.pending;
    t.breached   += a.breached;
    t.resolved   += a.resolved;
    return t;
  }, { scheduled:0, srHours:0, nonSrHours:0, totalHours:0, srCount:0, nonSrCount:0, total:0, pending:0, breached:0, resolved:0 });
  const overallSla = (totals.resolved + totals.breached) > 0
    ? +((totals.resolved) / (totals.resolved + totals.breached) * 100).toFixed(2)
    : 0;

  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: '16px 18px', animation: 'fadeSlideUp 0.55s ease both' }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: '#1e3a8a', textAlign: 'center', marginBottom: 4 }}>Ticket Categories Breakdown ({monthLabel}) — By Utilization, SLA%</div>
      <div style={{ fontSize: 11, color: C.muted, textAlign: 'center', marginBottom: 14 }}>Total scheduled hours per agent = working days × scheduled hours − approved leaves</div>

      {/* 3-up bar charts */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 16 }}>
        {/* SRs + Non-SRs Handled */}
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 8, textAlign: 'center' }}>SRs + Non-SRs Handled Per Agent</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {agents.map((a, i) => (
              <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 110, fontSize: 10, color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</div>
                <div style={{ flex: 1, display: 'flex', height: 14, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ width: `${(a.srCount / maxHandled) * 100}%`, background: '#86c34a' }} title={`SRs: ${a.srCount}`} />
                  <div style={{ width: `${(a.nonSrCount / maxHandled) * 100}%`, background: '#3b82f6' }} title={`Non-SRs: ${a.nonSrCount}`} />
                </div>
                <div style={{ width: 60, fontSize: 10, color: C.text, textAlign: 'right' }}>
                  <span style={{ color: '#86c34a', fontWeight: 700 }}>{a.srCount}</span>/<span style={{ color: '#3b82f6', fontWeight: 700 }}>{a.nonSrCount}</span>
                </div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 6, fontSize: 10, color: C.muted, display: 'flex', gap: 10, justifyContent: 'center' }}>
            <span><span style={{ color: '#86c34a' }}>■</span> SRs</span>
            <span><span style={{ color: '#3b82f6' }}>■</span> Non-SRs</span>
          </div>
        </div>

        {/* Total Utilization % */}
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 8, textAlign: 'center' }}>Total Utilization %</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {agents.map((a, i) => (
              <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 110, fontSize: 10, color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</div>
                <div style={{ flex: 1, background: '#f1f5f9', borderRadius: 3, height: 14, overflow: 'hidden' }}>
                  <div style={{ width: `${Math.min((a.utilizationPct / maxUtil) * 100, 100)}%`, height: '100%', background: colors[i % colors.length] }} />
                </div>
                <div style={{ width: 48, fontSize: 10, fontWeight: 700, textAlign: 'right', color: a.utilizationPct >= 75 ? C.green : a.utilizationPct >= 40 ? C.amber : C.red }}>{a.utilizationPct.toFixed(2)}</div>
              </div>
            ))}
          </div>
        </div>

        {/* SLA % Compliance */}
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 8, textAlign: 'center' }}>SLA % Compliance by Agent</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {agents.map((a, i) => (
              <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 110, fontSize: 10, color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</div>
                <div style={{ flex: 1, background: '#f1f5f9', borderRadius: 3, height: 14, overflow: 'hidden' }}>
                  <div style={{ width: `${a.slaPct}%`, height: '100%', background: a.slaPct >= 95 ? C.green : a.slaPct >= 80 ? C.amber : C.red }} />
                </div>
                <div style={{ width: 48, fontSize: 10, fontWeight: 700, textAlign: 'right', color: a.slaPct >= 95 ? C.green : a.slaPct >= 80 ? C.amber : C.red }}>{a.slaPct.toFixed(2)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Detailed Table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, minWidth: 1100 }}>
          <thead>
            <tr style={{ background: '#1e3a8a', color: '#fff' }}>
              <th style={{ ...thS, textAlign: 'left' }}>Action Taker 1</th>
              <th style={thS}>Total Work Hours (hr)</th>
              <th style={thS}>SR Time Spent (Hrs)</th>
              <th style={thS}>Non-SRs (Hrs)</th>
              <th style={thS}>Total Spent</th>
              <th style={thS}>Non-SRs</th>
              <th style={thS}>Non-SRs Util %</th>
              <th style={thS}>SRs</th>
              <th style={thS}>SR Util %</th>
              <th style={thS}>Total Util %</th>
              <th style={thS}>Count</th>
              <th style={thS}>Avg Resol</th>
              <th style={thS}>Pending</th>
              <th style={thS}>SLA %</th>
              <th style={thS}>Avg CSAT</th>
            </tr>
          </thead>
          <tbody>
            {agents.map(a => (
              <tr key={a.id}>
                <td style={{ ...tdS, fontWeight: 700 }}>{a.name}</td>
                <td style={{ ...tdS, textAlign: 'center' }}>{a.scheduledHours.toFixed(1)}</td>
                <td style={{ ...tdS, textAlign: 'center' }}>{a.srHours.toFixed(2)}</td>
                <td style={{ ...tdS, textAlign: 'center' }}>{a.nonSrHours.toFixed(2)}</td>
                <td style={{ ...tdS, textAlign: 'center' }}>{a.totalHours.toFixed(2)}</td>
                <td style={{ ...tdS, textAlign: 'center' }}>{a.nonSrCount}</td>
                <td style={{ ...tdS, textAlign: 'center' }}>{a.nonSrUtilizationPct.toFixed(2)}</td>
                <td style={{ ...tdS, textAlign: 'center' }}>{a.srCount}</td>
                <td style={{ ...tdS, textAlign: 'center' }}>{a.srUtilizationPct.toFixed(2)}</td>
                <td style={{ ...tdS, textAlign: 'center', fontWeight: 700, color: a.utilizationPct >= 75 ? C.green : C.amber }}>{a.utilizationPct.toFixed(2)}</td>
                <td style={{ ...tdS, textAlign: 'center' }}>{clickNum(a.total, a.name, false)}</td>
                <td style={{ ...tdS, textAlign: 'center' }}>{a.avgResolutionHrs != null ? a.avgResolutionHrs.toFixed(2) : '—'}</td>
                <td style={{ ...tdS, textAlign: 'center' }}>{clickNum(a.pending, a.name, true)}</td>
                <td style={{ ...tdS, textAlign: 'center', fontWeight: 700, color: a.slaPct >= 95 ? C.green : a.slaPct >= 80 ? C.amber : C.red }}>{a.slaPct.toFixed(2)}</td>
                <td style={{ ...tdS, textAlign: 'center' }}>{a.avgCsat != null ? a.avgCsat.toFixed(2) : '—'}</td>
              </tr>
            ))}
            <tr style={{ background: '#dbeafe', fontWeight: 800 }}>
              <td style={tdS}>Grand Total</td>
              <td style={{ ...tdS, textAlign: 'center' }}>{totals.scheduled.toFixed(1)}</td>
              <td style={{ ...tdS, textAlign: 'center' }}>{totals.srHours.toFixed(2)}</td>
              <td style={{ ...tdS, textAlign: 'center' }}>{totals.nonSrHours.toFixed(2)}</td>
              <td style={{ ...tdS, textAlign: 'center' }}>{totals.totalHours.toFixed(2)}</td>
              <td style={{ ...tdS, textAlign: 'center' }}>{totals.nonSrCount}</td>
              <td style={tdS} />
              <td style={{ ...tdS, textAlign: 'center' }}>{totals.srCount}</td>
              <td style={tdS} />
              <td style={tdS} />
              <td style={{ ...tdS, textAlign: 'center' }}>{totals.total}</td>
              <td style={tdS} />
              <td style={{ ...tdS, textAlign: 'center' }}>{totals.pending}</td>
              <td style={{ ...tdS, textAlign: 'center', color: overallSla >= 95 ? C.green : overallSla >= 80 ? C.amber : C.red }}>{overallSla.toFixed(2)}</td>
              <td style={tdS} />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
